<template>
	<div class="time-field">
		<v-text-field
				ref="timeInput"
				v-bind="{ ...defaultAttrs, ...$attrs }"
				v-model="computedTime"
				:clearable="$vuetify.breakpoint.mdAndUp"
				:disabled="disabled"
				max-length="5"
				:prepend-inner-icon="$vuetify.breakpoint.mdAndUp ? 'mdi-clock-outline' : null"
				:rules="timeRules"
				@click:clear="clear"
				@input="onTimeInputChange"
				@keydown="preventMultipleColons"
		>
			<template v-slot:append>
				<v-btn-toggle
						v-model="activePeriod"
						active-class="primary--text"
						background-color="transparent"
						borderless
						dense
						:disabled="disabled"
						mandatory
						@change="onPeriodChange"
				>
					<v-btn
							class="px-0"
							:disabled="disabled"
							:small="$vuetify.breakpoint.mdAndUp"
							:style="{
								marginTop: $vuetify.breakpoint.mdAndUp ? '-2px' : 0
							}"
							value="AM"
							:x-small="$vuetify.breakpoint.smAndDown"
					>
						AM
					</v-btn>
					<v-btn
							class="px-0"
							:disabled="disabled"
							:small="$vuetify.breakpoint.mdAndUp"
							:style="{
								marginTop: $vuetify.breakpoint.mdAndUp ? '-2px' : 0,
								marginRight: $vuetify.breakpoint.mdAndUp ? '0' : '-2px',
							}"
							value="PM"
							:x-small="$vuetify.breakpoint.smAndDown"
					>
						PM
					</v-btn>
				</v-btn-toggle>
			</template>
		</v-text-field>
	</div>
</template>

<script>
export default {
	name: 'BwTimeInput',
	inheritAttrs: false,
	props: {
		disabled: {
			type: Boolean,
			default: false,
		},
		required: Boolean,
		timeFormat: {
			type: String,
			default: 'HHmm'
		},
		value: {
			type: String,
			default: '',
		}
	},
	data() {
		return {
			activePeriod: '',
			displayedTime: '',
			requiredRule: v => !!v || 'Time is required',
			timeFormatRule: v => this.timeFormatValidRegex.test(v) || 'Invalid time entered',
			timeFormatValidRegex: /^(0?[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/
		};
	},
	computed: {
		computedTime: {
			get() {
				if(!this.displayedTime.length) this.updateDisplayedTime(this.value);
				return this.displayedTime;
			},
			set(newValue) {
				this.onTimeInputChange(newValue);
			},
		},
		defaultAttrs() {
			return {
				label: 'Time',
			};
		},
		timeRules() {
			const requiredRule = this.$attrs.required && this.requiredRule() || null;
			return [
				...(this.rules || []),
				...(this.$props.required ? [this.requiredRule] : []),
				this.timeFormatRule
			];
		}
	},
	methods: {
		changeTimeFormat() {
			if(this.displayedTime.length === 5 && /^[0-1]\d:[0-5]\d$/.test(this.displayedTime)) {
				const displayedHours = parseInt(this.displayedTime.slice(0, 2), 10);
				const minutes = this.displayedTime.slice(3);

				const newHours = this.activePeriod === 'PM'
						? displayedHours + 12
						: displayedHours - 12;
				const newTimeValue = `${newHours.toString().padStart(2, '0')}${minutes}`;
				const formattedValue = this.formatReturnValue(newTimeValue);
				this.$emit('input', formattedValue);
				this.updateDisplayedTime(newTimeValue);
			}
		},
		clear() {
			this.displayedTime = '';
			this.$emit('update:time', '');
		},
		formatReturnValue(value) {
			return moment(value, 'HHmm').format('HHmm');
		},
		onPeriodChange(newPeriod) {
			this.activePeriod = newPeriod;
			this.updateTime();
		},
		preventMultipleColons(event) {
			const newValue = event.target.value;
			if(newValue.includes(':') && event.key === ':') {
				event.preventDefault();
			}
		},
		onTimeInputChange(newValue) {
			if(!newValue) {
				this.$emit('input', '');
				this.displayedTime = '';
				return;
			}

			const parts = newValue.split(':');
			const hours = parseInt(parts[0], 10);
			const displayedHours = isNaN(hours)
					? ''
					: (hours % 12 === 0 ? 12 : hours % 12).toString();
			const displayedMinutes = parts.length > 1 ? parts[1] : '';

			if(newValue === '0') {
				this.displayedTime = '0';
			}
			else if(
					newValue.length === 5 &&
					/^[0-1]\d:[0-5]\d$/.test(newValue)
			) {
				const newTimeValue = `${hours.toString().padStart(2, '0')}${displayedMinutes}`;
				const formattedValue = this.formatReturnValue(newTimeValue);
				this.updateDisplayedTime(newTimeValue, false);
			}
			else if(
					newValue.length === 2 &&
					/^[0-2]\d$/.test(newValue) &&
					parseInt(newValue, 10) >= 13 &&
					parseInt(newValue, 10) <= 23 &&
					this.displayedTime.length < 5
			) {
				const adjustedHours = hours % 12 === 0 ? 12 : hours % 12;
				this.activePeriod = 'PM';
				this.displayedTime = `${adjustedHours}:`;
			}
			else if(
					hours > 23 ||
					(displayedMinutes.length === 2 && parseInt(displayedMinutes, 10) > 59)
			) {
				// Do nothing if input is invalid
			}
			else {
				this.displayedTime = `${displayedHours}${
						displayedMinutes ? ':' + displayedMinutes : ''
				}`;
			}
			this.updateTime();
		},
		updateTime() {
			const time = this.computedTime;
			const isValid = this.timeFormatValidRegex.test(time);

			// Emit value if it's valid
			if(isValid) {
				this.$emit('input', moment(`${time} ${this.activePeriod}`, 'HHmm A').format(this.timeFormat));
			}
		},
		updateDisplayedTime(timeValue, updatePeriod = true) {
			if(!timeValue) return;
			const hours = parseInt(timeValue.slice(0, 2), 10);
			const minutes = timeValue.slice(2);
			const displayedHours = (hours % 12 === 0 ? 12 : hours % 12).toString();
			this.displayedTime = `${displayedHours}:${minutes}`;

			if(updatePeriod) {
				this.activePeriod = hours >= 12 ? 'PM' : 'AM';
			}
		}
	},
	// mounted() {
	// 	this.updateDisplayedTime(this.value);
	// },
	watch: {
		value: {
			immediate: true,
			handler(newValue) {
				this.updateDisplayedTime(newValue);
			},
		},
	}
}
</script>